/**
 * OCC (Oklahoma Corporation Commission) unit overlays for the deals map.
 * Pure helpers — no React, no network.
 *
 * Units come from GET /api/spatial/occ/units?state=OK&county=…&kinds=…, one
 * Feature per unit with `properties.kind` set to one of OCC_KINDS.
 */

/** API `kind` values, in display order. These are identifiers, not labels. */
export const OCC_KINDS = [
  "pooling",
  "spacing",
  "increased_density",
  "location_exception",
];

/**
 * Display names for the `kind` values.
 *
 * KNOWN GAP: these are source literals. The `occ_units` metadata labels the
 * `kind` FIELD but has nowhere to label its VALUES, and there is no pick list
 * for them. Move to data (a pick list, or a metadata lookup) when one exists.
 */
export const OCC_KIND_LABELS = {
  pooling: "Pooling",
  spacing: "Spacing",
  increased_density: "Increased density",
  location_exception: "Location exception",
};

export const occKindLabel = (kind) => OCC_KIND_LABELS[kind] ?? String(kind ?? "");

/**
 * The "intents to drill" (well permits) option in the same OCC control. Not
 * a unit kind — it is NOT in OCC_KINDS, which drives the /occ/units `kinds`
 * param; intents come from GET /api/spatial/occ/intents (utils/occIntents).
 *
 * KNOWN GAP: the label is a source literal, like OCC_KIND_LABELS above —
 * there is no data source for the control's option labels.
 */
export const OCC_INTENTS_OPTION = "intents";
export const OCC_INTENTS_LABEL = "Intents to drill";

/** True for an OCC unit feature's property bag. */
export const isOccUnitProps = (props) => OCC_KINDS.includes(props?.kind);

/** Only the OCC unit endpoint's state is supported. */
export const OCC_STATE = "OK";

/**
 * Split a FeatureCollection into one FC per requested kind. Every requested
 * kind gets an entry (empty when the API returned none) so the caller can
 * cache "asked, nothing there" as distinct from "not asked yet".
 *
 * @param {object} fc  FeatureCollection
 * @param {string[]} kinds
 * @returns {Record<string, object>}
 */
export const splitUnitsByKind = (fc, kinds) => {
  const out = {};
  for (const k of Array.isArray(kinds) ? kinds : []) {
    out[k] = { type: "FeatureCollection", features: [] };
  }
  for (const f of Array.isArray(fc?.features) ? fc.features : []) {
    const k = f?.properties?.kind;
    if (out[k]) out[k].features.push(f);
  }
  return out;
};
